import { useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Mail,
  FileText,
} from 'lucide-react';

import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { analyticsOverview } from '../services/api.js';
import {
  leads as allLeads,
  agents,
  showings as allShowings,
  verifications as allVerifications,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const DAY_MS = 86_400_000;
const WEEKS_AVAILABLE = 8;

// ── week math ────────────────────────────────────────────────────────────────
// Weeks are Monday → Sunday in local time.

function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // getDay: Sun=0, Mon=1, … Sat=6. Offset so Monday is 0.
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}

function endOfWeek(start) {
  const d = new Date(start);
  d.setDate(d.getDate() + 7);
  return d;
}

function weekKey(date) {
  const s = startOfWeek(date);
  return s.toISOString().slice(0, 10);
}

function buildWeekOptions() {
  const thisWeekStart = startOfWeek(NOW);
  const options = [];
  for (let i = 0; i < WEEKS_AVAILABLE; i++) {
    const start = new Date(thisWeekStart.getTime() - i * 7 * DAY_MS);
    const end = new Date(start.getTime() + 6 * DAY_MS);
    options.push({
      value: start.toISOString().slice(0, 10),
      start,
      end,
      label:
        i === 0
          ? 'This Week'
          : i === 1
            ? 'Last Week'
            : `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
    });
  }
  return options;
}

function formatRange(start, end) {
  const endInclusive = new Date(end.getTime() - 1);
  const sameMonth =
    start.getMonth() === endInclusive.getMonth() &&
    start.getFullYear() === endInclusive.getFullYear();
  if (sameMonth) {
    const month = start.toLocaleDateString(undefined, { month: 'long' });
    return `${month} ${start.getDate()} – ${endInclusive.getDate()}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${endInclusive.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function inWindow(iso, start, end) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t < end.getTime();
}

function formatCurrency(n) {
  if (!n) return '$0';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function formatMinutes(mins) {
  if (mins === null || mins === undefined || Number.isNaN(mins)) return '—';
  if (mins < 1) return '<1m';
  if (mins < 60) return `${Math.round(mins)}m`;
  const hours = Math.floor(mins / 60);
  const remainder = Math.round(mins - hours * 60);
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

const QUALIFIED_STATUSES = new Set([
  'qualified',
  'verified',
  'showing',
  'closed_won',
]);
const PIPELINE_STATUSES = new Set([
  'qualifying',
  'qualified',
  'verified',
  'showing',
  'nurture',
]);

// ── metrics ──────────────────────────────────────────────────────────────────

function computeMetrics(start, end) {
  const leadsReceived = allLeads.filter((l) =>
    inWindow(l.createdAt, start, end),
  );

  const leadsQualified = allLeads.filter(
    (l) =>
      QUALIFIED_STATUSES.has(l.status) &&
      inWindow(l.updatedAt || l.createdAt, start, end),
  );

  const leadsVerified = allVerifications.filter((v) =>
    inWindow(v.verifiedAt || v.createdAt, start, end),
  );

  const showingsBooked = allShowings.filter((s) =>
    inWindow(s.scheduledAt, start, end),
  );

  const showingsCompleted = allShowings.filter(
    (s) => s.status === 'completed' && inWindow(s.completedAt, start, end),
  );

  const noShows = allShowings.filter(
    (s) => s.status === 'no_show' && inWindow(s.scheduledAt, start, end),
  );

  // Response time: average of agent stats, adjusted for display.
  const responseTimes = agents
    .map((a) => a.stats?.avgResponseMinutes)
    .filter((v) => typeof v === 'number' && v > 0);
  const avgResponseMinutes = responseTimes.length
    ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
    : null;

  const pipelineValue = allLeads
    .filter((l) => PIPELINE_STATUSES.has(l.status))
    .reduce((sum, l) => sum + (l.budget?.max || l.budget?.min || 0), 0);

  return {
    leadsReceived: leadsReceived.length,
    leadsQualified: leadsQualified.length,
    leadsVerified: leadsVerified.length,
    showingsBooked: showingsBooked.length,
    showingsCompleted: showingsCompleted.length,
    noShows: noShows.length,
    avgResponseMinutes,
    pipelineValue,
  };
}

// ── UI pieces ────────────────────────────────────────────────────────────────

function MetricRow({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border-card last:border-b-0">
      <div>
        <p className="text-sm text-text-primary font-medium">{label}</p>
        {hint && <p className="text-xs text-text-secondary mt-0.5">{hint}</p>}
      </div>
      <span className="text-xl font-semibold text-text-primary tabular-nums">
        {value}
      </span>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Reports() {
  const { showToast } = useToast();
  const weekOptions = useMemo(() => buildWeekOptions(), []);
  const [selectedKey, setSelectedKey] = useState(weekOptions[0].value);
  const [generating, setGenerating] = useState(false);
  const printRef = useRef(null);

  const selected = weekOptions.find((w) => w.value === selectedKey) || weekOptions[0];
  const end = endOfWeek(selected.start);

  // Pull analytics overview — the API shape is sparse today so the seed
  // computation still drives most cells; the API values override when present.
  const overview = useApiData(analyticsOverview, []);
  const seedMetrics = useMemo(
    () => computeMetrics(selected.start, end),
    [selected.start, end],
  );
  const apiCards = overview.data?.cards || {};
  const metrics = {
    ...seedMetrics,
    leadsReceived: apiCards.leads_received ?? seedMetrics.leadsReceived,
    leadsQualified: apiCards.leads_qualified ?? seedMetrics.leadsQualified,
    leadsVerified: apiCards.leads_verified ?? seedMetrics.leadsVerified,
    showingsBooked: apiCards.showings_booked ?? seedMetrics.showingsBooked,
    showingsCompleted: apiCards.showings_completed ?? seedMetrics.showingsCompleted,
    noShows: apiCards.no_shows ?? seedMetrics.noShows,
    avgResponseMinutes:
      apiCards.avg_response_minutes ?? seedMetrics.avgResponseMinutes,
    pipelineValue: apiCards.pipeline_value ?? seedMetrics.pipelineValue,
  };

  const currentIndex = weekOptions.findIndex((w) => w.value === selectedKey);
  const goPrev = () => {
    const next = weekOptions[currentIndex + 1];
    if (next) setSelectedKey(next.value);
  };
  const goNext = () => {
    const next = weekOptions[currentIndex - 1];
    if (next) setSelectedKey(next.value);
  };

  const handleDownloadPdf = () => {
    if (generating) return;
    setGenerating(true);
    // Defer the print() call a tick so React flushes the disabled/label
    // update before the blocking print dialog opens. Reset on next tick
    // after the dialog closes so the button re-enables without polling.
    window.setTimeout(() => {
      try {
        window.print();
        showToast('Report ready to print or save as PDF', { type: 'success' });
      } catch (err) {
        showToast('Could not open the print dialog', { type: 'error' });
      } finally {
        setGenerating(false);
      }
    }, 50);
  };

  const handleEmail = () => {
    showToast('Email delivery coming soon.', { type: 'info' });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between print:hidden">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="page-title mb-0">Weekly Report</h1>
            {overview.loading && <InlineSpinner />}
          </div>
          <p className="page-subtitle mb-0 mt-2">
            Performance snapshot. Pick a week, review the numbers, export if
            you need to share.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generating}
            style={{ borderColor: '#EA580C', color: '#EA580C', borderWidth: '1.5px' }}
            className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                       hover:bg-[rgba(234,88,12,0.10)]
                       px-4 py-1.5 text-[13px] font-medium transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={13} /> {generating ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={handleEmail}
            style={{ borderColor: '#9CA3AF', color: '#6B7280', borderWidth: '1.5px' }}
            className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                       hover:bg-[rgba(107,114,128,0.10)] hover:text-[#333333]
                       px-4 py-1.5 text-[13px] font-medium transition-colors"
          >
            <Mail size={13} /> Send via Email
          </button>
        </div>
      </header>

      {overview.error && (
        <div className="print:hidden">
          <ErrorBanner
            message="Couldn’t load analytics from the server."
            onRetry={overview.refetch}
          />
        </div>
      )}

      {/* Week selector */}
      <div className="card p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between print:hidden">
        <label className="flex items-center gap-3 min-w-0">
          <span className="text-xs uppercase tracking-wider text-text-secondary whitespace-nowrap">
            Reporting week
          </span>
          <select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            className="bg-bg-card border border-border-card rounded-btn text-text-primary
                       text-sm px-3 py-2 hover:border-accent/60 focus:border-accent focus:outline-none
                       focus:ring-1 focus:ring-accent/50 transition-colors"
          >
            {weekOptions.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label} — {formatRange(w.start, endOfWeek(w.start))}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentIndex >= weekOptions.length - 1}
            className="inline-flex items-center gap-1 rounded-btn border border-border-card
                       text-sm px-3 py-1.5 text-text-primary hover:border-accent
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
            Previous
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={currentIndex === 0}
            className="inline-flex items-center gap-1 rounded-btn border border-border-card
                       text-sm px-3 py-1.5 text-text-primary hover:border-accent
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Report body */}
      <div ref={printRef} className="card p-6 md:px-7 md:py-7 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-accent">
              <FileText size={18} />
              <span className="text-xs uppercase tracking-wider font-medium">
                Weekly Performance
              </span>
            </div>
            <h2 className="mt-1 text-xl md:text-2xl font-semibold text-text-primary">
              {formatRange(selected.start, end)}
            </h2>
            <p className="text-sm text-text-secondary">
              Prepared {NOW.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </header>

        <section>
          <h3 className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Pipeline
          </h3>
          <MetricRow
            label="Leads Received This Week"
            value={metrics.leadsReceived}
            hint="New inbound contacts across all channels."
          />
          <MetricRow
            label="Leads Qualified"
            value={metrics.leadsQualified}
            hint="Moved past discovery into qualified, verified, or showing."
          />
          <MetricRow
            label="Leads Verified"
            value={metrics.leadsVerified}
            hint="Buyer Verification Scorecards completed this week."
          />
        </section>

        <section>
          <h3 className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Showings
          </h3>
          <MetricRow
            label="Showings Booked"
            value={metrics.showingsBooked}
            hint="Tours scheduled with a scheduled-at date in this week."
          />
          <MetricRow
            label="Showings Completed"
            value={metrics.showingsCompleted}
            hint="Tours the team actually ran this week."
          />
          <MetricRow
            label="No-Shows"
            value={metrics.noShows}
            hint="Scheduled tours where the lead did not show."
          />
        </section>

        <section>
          <h3 className="text-xs uppercase tracking-wider text-text-secondary mb-2">
            Team & Value
          </h3>
          <MetricRow
            label="Average Response Time"
            value={formatMinutes(metrics.avgResponseMinutes)}
            hint="Across all agents and ISAs."
          />
          <MetricRow
            label="Pipeline Value"
            value={formatCurrency(metrics.pipelineValue)}
            hint="Sum of top-of-range budgets for open pipeline."
          />
        </section>
      </div>

    </div>
  );
}
